import {
  PERFORMANCE_DETAIL_LIMIT,
  PERFORMANCE_TARGETS,
  activityGrain,
  type CountDifferenceRow,
  type LateOrderRow,
  type PackingErrorRow,
  type PerformanceDetail,
  type PerformanceMeasurement,
  type PerformanceReport,
  type PerformanceStart,
  type ShipByDefinition,
  type TenantSummary,
} from '@wms/domain';
import { useState, type ReactNode } from 'react';
import { api, errorMessage } from '../api';
import { fmt } from '../format';
import {
  GRAIN_TEXT,
  PERIOD_PRESETS,
  START_ZONE,
  bucketLabel,
  bucketTitle,
  detailMoreText,
  groupDays,
  measurementText,
  percentText as percent,
  wallClock,
  zonedToIso,
} from '../performance';
import { useSession } from '../session';
import { Empty, ErrorBanner, Modal, SellerChip, Spinner, useLoad, useToast } from '../ui';
import { Icon } from '../ui/icons';
import { Activity } from './performance/Activity';
import { ShipByDefinitionNote, ShipByDialog } from './performance/ShipByRule';

const tone = (rate: number | null, target: number) => (rate === null ? '' : rate >= target ? 'ok' : rate >= target - 0.03 ? 'warn' : 'bad');

/**
 * Performans: zamanında kargo, toplama doğruluğu, stok doğruluğu. The warehouse sees all sellers
 * or one; a seller sees its own orders and products (the numbers a fulfillment contract promises).
 */
export function Performance() {
  const { isOperator, isAdmin, warehouseId } = useSession();
  const [preset, setPreset] = useState('30');
  // days in the zone times are shown in (Ayarlar → Saat dilimi), not the device's
  const [range, setRange] = useState(() => PERIOD_PRESETS[1]!.range(fmt.today()));
  const [tenantId, setTenantId] = useState('');
  const [detail, setDetail] = useState<PerformanceDetail>('late');
  // a new or cleared Ölçüm başlangıcı: everything on the page is read again
  const [revision, setRevision] = useState(0);
  const [resetting, setResetting] = useState(false);
  const params = new URLSearchParams({ from: range.from, to: range.to });
  if (isOperator && warehouseId) params.set('warehouseId', warehouseId);
  if (isOperator && tenantId) params.set('tenantId', tenantId);
  const query = params.toString();
  const report = useLoad(() => api<PerformanceReport>('GET', `/api/performance?${query}`), [query, revision], 60_000);
  const tenants = useLoad(() => (isOperator ? api<TenantSummary[]>('GET', '/api/admin/tenants') : Promise.resolve([])), [isOperator]);
  // Zamanında kargo tanımı: this warehouse's; a seller's own warehouses'
  const definitions = useLoad(
    () => api<ShipByDefinition[]>('GET', `/api/performance/ship-by-rule${isOperator && warehouseId ? `?warehouseId=${warehouseId}` : ''}`),
    [isOperator, warehouseId, revision],
  );
  const [editing, setEditing] = useState<ShipByDefinition | null>(null);
  const changed = () => setRevision((n) => n + 1);
  const editable = isAdmin && warehouseId ? (definitions.data ?? []).find((d) => d.warehouseId === warehouseId) : undefined;

  return (
    <>
      <div className="row">
        {PERIOD_PRESETS.map((p) => (
          <button
            key={p.key}
            className={`chip select${preset === p.key ? ' on' : ''}`}
            onClick={() => {
              setPreset(p.key);
              setRange(p.range(fmt.today()));
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="row">
        <input className="input" type="date" style={{ maxWidth: 160 }} value={range.from} max={range.to} onChange={(e) => (setPreset(''), setRange({ ...range, from: e.target.value }))} aria-label="Başlangıç" />
        <input className="input" type="date" style={{ maxWidth: 160 }} value={range.to} min={range.from} onChange={(e) => (setPreset(''), setRange({ ...range, to: e.target.value }))} aria-label="Bitiş" />
        {isOperator && (
          <select className="input" style={{ maxWidth: 220 }} value={tenantId} onChange={(e) => setTenantId(e.target.value)} aria-label="Satıcı">
            <option value="">Tüm satıcılar</option>
            {(tenants.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} · {t.displayName}
              </option>
            ))}
          </select>
        )}
        {isAdmin && warehouseId && (
          <button className="btn sm" onClick={() => setResetting(true)} title="Başlangıçtan önceki hareket Performans’ta sayılmaz; hiçbir kayıt silinmez">
            <Icon name="refresh" size={16} /> Performansı sıfırla
          </button>
        )}
      </div>

      {report.error && <ErrorBanner message={report.error} onRetry={() => void report.reload()} />}
      {report.data && <MeasurementNote m={report.data.measurement} staff={isOperator} canClear={isAdmin} onCleared={changed} />}
      {!report.data ? (
        <Spinner />
      ) : report.data.measurement.periodBeforeStart ? (
        <section className="card">
          <Empty title="Bu dönem ölçüm başlangıcından önce bitiyor">
            <p>
              Performans {report.data.measurement.since ? fmt.stamp(report.data.measurement.since) : ''} itibarıyla sayılıyor; öncesindeki hareket silinmedi, yalnız
              burada sayılmıyor. Başlangıçtan sonraki bir dönem seçin, örneğin “Son 7 gün”.
            </p>
          </Empty>
        </section>
      ) : (
        <>
          <div className="grid perf-grid">
            <KpiCard
              title="Zamanında kargo"
              rate={report.data.onTime.rate}
              target={PERFORMANCE_TARGETS.onTime}
              lines={[
                `${fmt.n(report.data.onTime.due)} siparişten ${fmt.n(report.data.onTime.onTime)} tanesi zamanında kargoya verildi`,
                report.data.onTime.late + report.data.onTime.overdue > 0
                  ? `${fmt.n(report.data.onTime.late)} geç verildi · ${fmt.n(report.data.onTime.overdue)} hâlâ bekliyor`
                  : 'Geciken sipariş yok',
              ]}
              help={
                <>
                  <span>Son kargo zamanı bu dönemde dolan siparişler sayılır.</span>
                  {definitions.data && definitions.data.length > 0 && <ShipByDefinitionNote definitions={definitions.data} staff={isOperator} />}
                </>
              }
              onShow={() => setDetail('late')}
              actions={
                editable && (
                  <button className="btn sm ghost" onClick={() => setEditing(editable)}>
                    <Icon name="edit" size={16} /> Tanımı değiştir
                  </button>
                )
              }
            />
            <KpiCard
              title="Toplama doğruluğu"
              rate={report.data.pickAccuracy.rate}
              target={PERFORMANCE_TARGETS.pickAccuracy}
              lines={[
                `${fmt.n(report.data.pickAccuracy.packed)} paketlenen siparişten ${fmt.n(report.data.pickAccuracy.packed - report.data.pickAccuracy.withErrors)} tanesi hatasız`,
                `${fmt.n(report.data.pickAccuracy.wrongProductScans)} yanlış ürün · ${fmt.n(report.data.pickAccuracy.excessScans)} fazla adet okutması`,
              ]}
              help="Paketleme masasında okutmayla yakalanan yanlış ürün ve fazla adet hatası olmadan paketlenen siparişlerin oranı. Hatalar müşteriye gitmeden yakalanmıştır."
              onShow={() => setDetail('pick_errors')}
            />
            <KpiCard
              title="Stok doğruluğu"
              rate={report.data.stockAccuracy.rate}
              target={PERFORMANCE_TARGETS.stockAccuracy}
              lines={[
                `${fmt.n(report.data.stockAccuracy.lines)} sayılan kalemden ${fmt.n(report.data.stockAccuracy.linesMatched)} tanesi sistemle birebir tuttu`,
                report.data.stockAccuracy.unitRate === null
                  ? `${fmt.n(report.data.stockAccuracy.bins)} göz sayıldı`
                  : `${fmt.n(report.data.stockAccuracy.bins)} göz · adet sapması ${fmt.n(report.data.stockAccuracy.unitsOff)} / ${fmt.n(report.data.stockAccuracy.unitsExpected)} (${percent(report.data.stockAccuracy.unitRate)} isabet)`,
              ]}
              help="Bu dönemde sayılıp kapatılan gözlerdeki ürün ve lot kalemlerinden, sistemdeki adetle tutanların oranı (yeniden sayımda son tur geçerli)."
              onShow={() => setDetail('count_differences')}
            />
          </div>

          <DailyChart report={report.data} />

          <section className="card">
            <div className="card-h">
              {(
                [
                  ['late', 'Geciken siparişler'],
                  ['pick_errors', 'Paketlemede yakalanan hatalar'],
                  ['count_differences', 'Sayım farkları'],
                ] as const
              ).map(([key, label]) => (
                <button key={key} className={`chip select${detail === key ? ' on' : ''}`} onClick={() => setDetail(key)}>
                  {label}
                </button>
              ))}
            </div>
            <DetailTable key={`${detail}?${query}#${revision}`} detail={detail} query={query} operator={isOperator} total={detailTotal(report.data, detail)} />
          </section>

          <Activity
            key={revision}
            query={query}
            operator={isOperator}
            allSellers={isOperator && !tenantId}
            onSeller={(id) => {
              setTenantId(id);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </>
      )}
      {editing && (
        <ShipByDialog
          definition={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            changed();
          }}
        />
      )}
      {resetting && warehouseId && (
        <ResetDialog
          warehouseId={warehouseId}
          tenants={tenants.data ?? []}
          tenantId={tenantId}
          onClose={() => setResetting(false)}
          onSaved={() => {
            setResetting(false);
            changed();
          }}
        />
      )}
    </>
  );
}

/**
 * "Ölçüm başlangıcı: 24.10.2026 21:30 — öncesi sayılmıyor". The warehouse also sees which starts there are and
 * who set them; the depo yöneticisi can undo each one ("Sıfırlamayı geri al": the start it replaced, if any,
 * is in effect again).
 */
function MeasurementNote({ m, staff, canClear, onCleared }: { m: PerformanceMeasurement; staff: boolean; canClear: boolean; onCleared: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const text = measurementText(m, fmt.stamp);
  if (!text) return null;
  const starts = [...(m.warehouse ? [m.warehouse] : []), ...m.sellers];
  const clear = async (s: PerformanceStart) => {
    const whose = s.tenantCode ? `${s.tenantCode} satıcısının` : 'Deponun';
    const question = s.replacedStartsAt
      ? `${whose} ölçüm başlangıcı geri alınsın mı? Yerine geçtiği başlangıç (${fmt.stamp(s.replacedStartsAt)}) yeniden geçerli olur.`
      : `${whose} ölçüm başlangıcı kaldırılsın mı? Öncesindeki hareket Performans’ta yeniden sayılır.`;
    if (!window.confirm(question)) return;
    setBusy(true);
    try {
      const scope = new URLSearchParams({ warehouseId: s.warehouseId });
      if (s.tenantId) scope.set('tenantId', s.tenantId);
      await api('DELETE', `/api/performance/start?${scope.toString()}`);
      toast(
        s.replacedStartsAt
          ? `Sıfırlama geri alındı; ölçüm yine ${fmt.stamp(s.replacedStartsAt)} itibarıyla`
          : 'Sıfırlama geri alındı; öncesindeki hareket yeniden sayılıyor',
        'ok',
      );
      onCleared();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="banner info-soft perf-start" role="note">
      <Icon name="clock" />
      <div className="perf-start-b">
        <span>{text}</span>
        {staff &&
          starts.map((s) => (
            <span key={s.id} className="perf-start-row">
              <span>
                {s.tenantCode ? <SellerChip code={s.tenantCode} /> : <b>Tüm depo</b>} {fmt.stamp(s.startsAt)}
                <span className="muted">
                  {' '}
                  · {s.setBy ? `${s.setBy}, ` : ''}
                  {fmt.stamp(s.setAt)}
                  {s.replacedStartsAt ? ` · önceki ${fmt.stamp(s.replacedStartsAt)}` : ''}
                </span>
              </span>
              {canClear && (
                <button className="btn sm ghost" disabled={busy} onClick={() => void clear(s)}>
                  <Icon name="undo" size={16} /> Sıfırlamayı geri al
                </button>
              )}
            </span>
          ))}
      </div>
    </div>
  );
}

/** "Performansı sıfırla": from when the page counts, for the whole warehouse or one seller; nothing is deleted. */
function ResetDialog(props: { warehouseId: string; tenants: readonly TenantSummary[]; tenantId: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [scope, setScope] = useState<'warehouse' | 'seller'>(props.tenantId ? 'seller' : 'warehouse');
  const [tenantId, setTenantId] = useState(props.tenantId);
  const [when, setWhen] = useState<'now' | 'at'>('now');
  const [local, setLocal] = useState(() => wallClock(new Date(), START_ZONE));
  const [busy, setBusy] = useState(false);
  // every start of the warehouse, whichever seller the page is narrowed to: the one picked here may have its own
  const starts = useLoad(() => api<PerformanceStart[]>('GET', `/api/performance/starts?warehouseId=${props.warehouseId}`), [props.warehouseId]);
  const picked = scope === 'warehouse' || tenantId !== '';
  const current = (starts.data ?? []).find((s) => (scope === 'warehouse' ? s.tenantId === null : s.tenantId === tenantId)) ?? null;
  const warehouseStart = (starts.data ?? []).find((s) => s.tenantId === null) ?? null;
  const startsAt = when === 'now' ? null : zonedToIso(local, START_ZONE);
  const ahead = startsAt !== null && Date.parse(startsAt) > Date.now();
  // not before the start it would replace is known
  const ready = starts.data !== undefined && picked && (when === 'now' || (startsAt !== null && !ahead));
  const save = async () => {
    setBusy(true);
    try {
      await api('PUT', '/api/performance/start', { warehouseId: props.warehouseId, tenantId: scope === 'seller' ? tenantId : null, startsAt });
      toast('Performans sıfırlandı: başlangıçtan önceki hareket artık sayılmıyor', 'ok');
      props.onSaved();
    } catch (e) {
      toast(errorMessage(e), 'bad');
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Performansı sıfırla"
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || !ready} onClick={() => void save()}>
            Performansı sıfırla
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="banner info-soft perf-start" style={{ display: 'block' }}>
          <b>Hiçbir kayıt silinmez.</b> Siparişler, okutmalar, sayımlar, stok hareketleri, ücretler ve faturalar olduğu gibi kalır. Performans sayfası
          yalnız başlangıçtan sonraki hareketi sayar; öncesi bu sayfada sayılmaz. Satıcı da kendi Performans sayfasında aynı başlangıcı görür. Fikrinizi
          değiştirirseniz notun yanındaki “Sıfırlamayı geri al” bu sıfırlamayı geri alır: önceki başlangıç varsa o yeniden geçerli olur, yoksa öncesi yeniden
          sayılır.
        </div>
        <div className="field">
          <label>Kimin performansı</label>
          <div className="choice-list">
            <label className={`choice${scope === 'warehouse' ? ' on' : ''}`}>
              <input type="radio" name="perf-scope" checked={scope === 'warehouse'} onChange={() => setScope('warehouse')} />
              <span>
                <b>Tüm depo</b>
                <small>Bu depodaki bütün satıcılar. Gerçek müşterilerin önceki rakamları da sayılmaz olur.</small>
              </span>
            </label>
            <label className={`choice${scope === 'seller' ? ' on' : ''}`}>
              <input type="radio" name="perf-scope" checked={scope === 'seller'} onChange={() => setScope('seller')} />
              <span>
                <b>Bir satıcı</b>
                <small>Yalnız seçtiğiniz satıcı (örneğin deneme yaptığınız kendi firmanız); diğerlerinin geçmişi olduğu gibi sayılır.</small>
              </span>
            </label>
          </div>
          {scope === 'seller' && (
            <select className="input" value={tenantId} onChange={(e) => setTenantId(e.target.value)} aria-label="Satıcı">
              <option value="">Satıcı seçin</option>
              {props.tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} · {t.displayName}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="field">
          <label>Ne zamandan itibaren sayılsın</label>
          <div className="choice-list">
            <label className={`choice${when === 'now' ? ' on' : ''}`}>
              <input type="radio" name="perf-when" checked={when === 'now'} onChange={() => setWhen('now')} />
              <span>
                <b>Şimdiden</b>
                <small>Bu andan sonraki hareket sayılır.</small>
              </span>
            </label>
            <label className={`choice${when === 'at' ? ' on' : ''}`}>
              <input type="radio" name="perf-when" checked={when === 'at'} onChange={() => setWhen('at')} />
              <span>
                <b>Seçtiğim tarih ve saatten</b>
                <small>Örneğin denemelerin bittiği an. Türkiye saatiyle.</small>
              </span>
            </label>
          </div>
          {when === 'at' && (
            <input
              className="input"
              type="datetime-local"
              value={local}
              max={wallClock(new Date(), START_ZONE)}
              onChange={(e) => setLocal(e.target.value)}
              aria-label="Başlangıç tarihi ve saati"
              style={{ maxWidth: 240 }}
            />
          )}
          {ahead && <span className="hint bad">Başlangıç ileri bir zaman olamaz.</span>}
        </div>
        {starts.error && <ErrorBanner message={starts.error} onRetry={() => void starts.reload()} />}
        {starts.data && picked && (
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            {current
              ? `Şu anki başlangıç ${fmt.stamp(current.startsAt)}; yenisi onun yerine geçer. “Sıfırlamayı geri al” derseniz bu başlangıç geri gelir.`
              : scope === 'warehouse'
                ? 'Deponun şu an bir başlangıcı yok.'
                : `Bu satıcının şu an kendi başlangıcı yok${warehouseStart ? `; deponun başlangıcı ${fmt.stamp(warehouseStart.startsAt)} geçerli` : ''}.`}
          </p>
        )}
      </div>
    </Modal>
  );
}

function KpiCard(props: { title: string; rate: number | null; target: number; lines: readonly string[]; help: ReactNode; onShow: () => void; actions?: ReactNode }) {
  const t = tone(props.rate, props.target);
  return (
    <section className={`card kpi perf ${t}`}>
      <span className="l">{props.title}</span>
      <span className="n">{percent(props.rate)}</span>
      <span className="h">Hedef {percent(props.target)}</span>
      {props.lines.map((line) => (
        <span key={line} className="h">
          {line}
        </span>
      ))}
      <div className="h help">{props.help}</div>
      <div className="row" style={{ gap: 4 }}>
        <button className="btn sm ghost" onClick={props.onShow}>
          <Icon name="list" size={16} /> Ayrıntı
        </button>
        {props.actions}
      </div>
    </section>
  );
}

/** Day by day (by week or month over a long period): three thin bars each (share on time, clean packs, matching counts). */
function DailyChart({ report }: { report: PerformanceReport }) {
  type Day = PerformanceReport['days'][number];
  const series = [
    { key: 'onTime', label: 'Zamanında kargo', color: 'var(--brand)', rate: (d: Day) => (d.due ? d.onTime / d.due : null), n: (d: Day) => d.due },
    { key: 'pick', label: 'Hatasız paket', color: 'var(--ok)', rate: (d: Day) => (d.packed ? d.packedClean / d.packed : null), n: (d: Day) => d.packed },
    { key: 'count', label: 'Tutan sayım', color: 'var(--warn)', rate: (d: Day) => (d.linesCounted ? d.linesMatched / d.linesCounted : null), n: (d: Day) => d.linesCounted },
  ];
  const grain = activityGrain(report.from, report.to);
  const days = groupDays(report.days, grain);
  // a week's or a month's bars get the room of several days
  const width = Math.max(320, days.length * (grain === 'day' ? 18 : 48));
  const height = 120;
  const slot = width / days.length;
  const bar = Math.max(2, Math.min(5, slot / 4));
  if (days.every((d) => !d.due && !d.packed && !d.linesCounted)) return null;
  return (
    <section className="card">
      <div className="card-h">
        <Icon name="chart" />
        <h2>{GRAIN_TEXT[grain]}</h2>
        <span className="sub">
          {series.map((s) => (
            <span key={s.key} className="legend">
              <i style={{ background: s.color }} /> {s.label}
            </span>
          ))}
        </span>
      </div>
      <div className="card-b" style={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${width} ${height + 18}`} width={width} height={height + 18} role="img" aria-label="Günlük göstergeler">
          {[0.5, 1].map((y) => (
            <line key={y} x1={0} x2={width} y1={height - y * height} y2={height - y * height} stroke="var(--line)" strokeDasharray="3 3" />
          ))}
          {days.map((d, i) =>
            series.map((s, j) => {
              const r = s.rate(d);
              if (r === null) return null;
              const x = i * slot + slot / 2 + (j - 1) * (bar + 1) - bar / 2;
              return (
                <rect key={`${d.date}-${s.key}`} x={x} y={height - r * height} width={bar} height={Math.max(1, r * height)} fill={s.color} rx={1}>
                  <title>{`${bucketTitle({ from: d.date, to: d.to }, grain)} · ${s.label}: ${percent(r)} (${s.n(d)})`}</title>
                </rect>
              );
            }),
          )}
          {days.map((d, i) =>
            i % Math.ceil(days.length / 10) === 0 ? (
              <text key={d.date} x={i * slot + slot / 2} y={height + 14} fontSize={10} textAnchor="middle" fill="var(--ink-3)">
                {bucketLabel({ from: d.date }, grain)}
              </text>
            ) : null,
          )}
        </svg>
      </div>
    </section>
  );
}

/** How many rows behind a number its card counted (the list stops at the newest PERFORMANCE_DETAIL_LIMIT) */
function detailTotal(r: PerformanceReport, detail: PerformanceDetail): number {
  if (detail === 'late') return r.onTime.late + r.onTime.overdue;
  if (detail === 'pick_errors') return r.pickAccuracy.wrongProductScans + r.pickAccuracy.excessScans;
  return r.stockAccuracy.lines - r.stockAccuracy.linesMatched;
}

/** The first rows behind a number; the rest (the newest PERFORMANCE_DETAIL_LIMIT at most) on request, so a long period keeps the page short. */
const DETAIL_ROWS = 15;

function DetailTable({ detail, query, operator, total }: { detail: PerformanceDetail; query: string; operator: boolean; total: number }) {
  const rows = useLoad(() => api<unknown[]>('GET', `/api/performance/details?detail=${detail}&${query}`), [detail, query]);
  const [all, setAll] = useState(false);
  if (rows.error) return <ErrorBanner message={rows.error} onRetry={() => void rows.reload()} />;
  if (!rows.data) return <Spinner />;
  if (rows.data.length === 0) return <Empty title="Bu dönemde kayıt yok" />;
  const shown = all ? rows.data : rows.data.slice(0, DETAIL_ROWS);
  const text = detailMoreText(rows.data.length, PERFORMANCE_DETAIL_LIMIT, total);
  const more =
    shown.length < rows.data.length || text.note ? (
      <div className="card-b row">
        {shown.length < rows.data.length && (
          <button className="btn sm" onClick={() => setAll(true)}>
            {text.button}
          </button>
        )}
        {text.note && <span className="muted">{text.note}</span>}
      </div>
    ) : null;
  if (detail === 'late') {
    return (
      <div className="table-wrap">
        <table className="t">
          <thead>
            <tr>
              <th>Sipariş</th>
              {operator && <th>Satıcı</th>}
              <th>Son kargo zamanı</th>
              <th>Kargoya verildi</th>
              <th className="num">Gecikme</th>
            </tr>
          </thead>
          <tbody>
            {(shown as LateOrderRow[]).map((r) => (
              <tr key={r.orderId}>
                <td>
                  <a href={`#/siparisler?q=${encodeURIComponent(r.orderNumber)}`} className="mono">
                    {r.orderNumber}
                  </a>
                </td>
                {operator && (
                  <td>
                    <SellerChip code={r.tenantCode} />
                  </td>
                )}
                <td>{fmt.dateTime(r.dueAt)}</td>
                <td>{r.shippedAt ? fmt.dateTime(r.shippedAt) : <span className="chip bad">Hâlâ depoda</span>}</td>
                <td className="num">{r.hoursLate >= 48 ? `${fmt.dec(r.hoursLate / 24)} gün` : `${fmt.dec(r.hoursLate)} saat`}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {more}
      </div>
    );
  }
  if (detail === 'pick_errors') {
    return (
      <div className="table-wrap">
        <table className="t">
          <thead>
            <tr>
              <th>Zaman</th>
              <th>Sipariş</th>
              {operator && <th>Satıcı</th>}
              <th>Okutulan</th>
              <th>Hata</th>
              {operator && <th>Paketleyen</th>}
            </tr>
          </thead>
          <tbody>
            {(shown as PackingErrorRow[]).map((r, i) => (
              <tr key={`${r.orderId}-${i}`}>
                <td>{fmt.dateTime(r.at)}</td>
                <td className="mono">{r.orderNumber}</td>
                {operator && (
                  <td>
                    <SellerChip code={r.tenantCode} />
                  </td>
                )}
                <td className="mono">{r.barcode}</td>
                <td>
                  <span className="chip warn">{r.result === 'wrong_product' ? 'Yanlış ürün' : 'Fazla adet'}</span>
                </td>
                {operator && <td>{r.packer ?? '—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
        {more}
      </div>
    );
  }
  return (
    <div className="table-wrap">
      <table className="t">
        <thead>
          <tr>
            <th>Kapanış</th>
            <th>Sayım</th>
            {operator && <th>Göz</th>}
            {operator && <th>Satıcı</th>}
            <th>Ürün</th>
            <th>Lot</th>
            <th className="num">Sistem</th>
            <th className="num">Sayılan</th>
            <th className="num">Fark</th>
          </tr>
        </thead>
        <tbody>
          {(shown as CountDifferenceRow[]).map((r, i) => (
            <tr key={`${r.taskNumber}-${r.sku}-${i}`}>
              <td>{fmt.dateTime(r.closedAt)}</td>
              <td className="mono">{r.taskNumber}</td>
              {operator && <td className="mono">{r.location}</td>}
              {operator && (
                <td>
                  <SellerChip code={r.tenantCode} />
                </td>
              )}
              <td>
                <b className="mono">{r.sku}</b>
                <div className="muted" style={{ fontSize: 12 }}>
                  {r.name}
                </div>
              </td>
              <td className="mono">{r.lotNo ?? '—'}</td>
              <td className="num">{fmt.n(r.expected)}</td>
              <td className="num">{fmt.n(r.counted)}</td>
              <td className="num">
                <span className={`chip ${r.difference < 0 ? 'bad' : 'warn'}`}>{r.difference > 0 ? `+${r.difference}` : r.difference}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {more}
    </div>
  );
}
